import React, { useState, useRef, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowDown } from "lucide-react";
import BaseAppLayout from "../../layouts/BaseAppLayout/BaseAppLayout";
import LibrarySearchBar from "../../components/LibrarySearchBar/LibrarySearchBar";
import Button from "../../components/Button/Button";
import { Toaster } from "react-hot-toast";
import { useResearchAPI } from "./useResearchAPI";
import { useScrollObserver } from "./useScrollObserver";
import ResearchStarterView from "./ResearchStarterView";
import ResearchChatView from "./ResearchChatView";

export default function Research() {
  // Search related state
  const [searchValue, setSearchValue] = useState("");
  const [searchResponses, setSearchResponses] = useState([]);
  const [currentPrompt, setCurrentPrompt] = useState(null);
  const [selectedChat, setSelectedChat] = useState(null);
  const [selectedDocumentsChat, setSelectedDocumentsChat] = useState(null);

  // Container refs for scrolling
  const containerRef = useRef(null);
  const latestSearchResultRef = useRef(null);

  // Custom hooks
  const {
    loading,
    chatId,
    urlSearchQuery,
    searchData,
    callSearchAPI,
    getSingleChatContent,
  } = useResearchAPI({
    setSearchResponses,
    setCurrentPrompt,
    setSelectedChat,
    setSelectedDocumentsChat,
  });

  const { showScrollButton } = useScrollObserver(null, latestSearchResultRef);

  // React-router hooks
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Initialize from URL params (once on mount)
  useEffect(() => {
    const chatParam = searchParams.get("chat");
    const queryParam = searchParams.get("query");

    if (chatParam && !queryParam) {
      if (queryParam && queryParam.trim()) {
        // Call the API with the search text from URL
        callSearchAPI(queryParam);
      }

      (async () => {
        const result = await getSingleChatContent(chatParam);
        if (!result || !result.fullHistory) return;

        const responses = result.fullHistory.map((h) => ({
          aiContent: h.parsedChat.ai_response,
          documents: h.parsedChat.documents || [],
          images: h.parsedChat.images || [],
          urls: h.parsedChat.urls || [],
          semantic_scholar_results: h.parsedChat.semantic_scholar_results || [],
        }));

        console.log("Responses from chat:", responses);

        setSearchResponses(responses);
        setSelectedChat(null);
      })();
    }
  }, [chatId, searchParams]);


  // Scroll to latest response when responses update
  useEffect(() => {
    if (latestSearchResultRef.current) {
      scrollLastResponseIntoView();
    }
  }, [searchResponses, loading]);

  // Unified search handler
  const handleSearch = useCallback((searchText) => {
    if (!searchText.trim()) return;
    
    // Update URL to include only the chat ID (if any)
    // We intentionally don't add the query parameter to keep URLs clean
    if (chatId) {
      navigate(`/client/research?chat=${chatId}`);
    } else {
      navigate(`/client/research`);
    }
    
    // Call the API with the search text
    callSearchAPI(searchText);
    
    // Reset search value
    setSearchValue("");
  }, [callSearchAPI, chatId, navigate]);

  // Scroll to the latest response smoothly
  const scrollLastResponseIntoView = () => {
    if (latestSearchResultRef.current) {
      latestSearchResultRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const showStarterView =
    !urlSearchQuery && searchResponses.length === 0 && !selectedChat;

  return (
    <BaseAppLayout headerText="Ricerca">
      <Toaster />

      {showStarterView ? (
        <ResearchStarterView
          searchData={searchData}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          handleSearch={handleSearch}
        />
      ) : (
        <div className="w-full relative h-fit">
          <div className="w-full relative bg-gray-50">
            <ResearchChatView
              selectedChat={selectedChat}
              selectedDocumentsChat={selectedDocumentsChat}
              searchResponses={searchResponses}
              currentPrompt={currentPrompt}
              containerRef={containerRef}
              latestSearchResultRef={latestSearchResultRef}
              handleSearch={handleSearch}
              loading={loading}
            />

            {/* Search Bar for new prompts */}
            <div className="md:w-2/3 w-full sticky bottom-0 md:bottom-[-1px] left-0 z-40 flex gap-0 border-r-2 border-gray-150">
              <div className="w-full flex flex-col gap-5">
                <AnimatePresence>
                  {showScrollButton && (
                    <motion.div
                      className="flex justify-center pointer-events-auto"
                      initial={{ opacity: 0, y: 20, scale: 0.5 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 20, scale: 0.5 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Button
                        type="bw"
                        icon={<ArrowDown size={16} />}
                        onClick={scrollLastResponseIntoView}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
                <div className="w-full h-full bg-gray-50 md:px-12 px-6 pb-8 pointer-events-auto">
                  <LibrarySearchBar
                    value={searchValue}
                    onChange={setSearchValue}
                    onSearch={handleSearch}
                    fullWidth
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </BaseAppLayout>
  );
}